import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { concat, Observable, of, OperatorFunction } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';

import { GroupsBackend } from '@libs/src/groups/groups.backend';

import { UploadCsvListDrawerComponent } from '@main-client/src/app/upload-csv-list-drawer/upload-csv-list-drawer.component';

import {
  AddGroupMatchConfigSegmentsSuccess,
  ReFetchGroupMatchConfigs,
  RemoveGroupMatchConfigSegmentsSuccess,
  UpdateGroupMatchConfigCustomSegmentConfigsSuccess,
  UpdateGroupMatchConfigSegmentsSuccess,
} from '@libs/src/group-match-config/group-match-config.actions';
import {
  UpdateGroupRoleSuccess,
  UpdateGroupSuccess,
} from '@libs/src/groups/groups-store/groups.actions';
import {
  addMatchSegmentInMatchConfig,
  removeMatchSegmentInMatchConfig,
  updateCustomSegmentConfigInMatchConfig,
  updateMatchSegmentInMatchConfig,
} from '@libs/src/match-config/match-config.helper';
import {
  Group,
  GroupCustomSegment,
  GroupCustomSegmentNode,
  GroupRole,
} from '@libs/src/models/group.model';
import {
  MatchConfig,
  MatchConfigCustomSegmentConfig,
  MatchSegment,
} from '@libs/src/models/match.interfaces';
import { IAppState } from '@main-client/src/app/app.state';
import {
  csvTemplateTitle,
  customSegmentAddRulesCsvTemplateMap,
  customSegmentCsvTemplateMap,
  customSegmentDeleteRulesCsvTemplateMap,
  customSegmentItemsCsvTemplateData,
  customSegmentRulesAddCsvTemplateData,
  customSegmentRulesDeleteCsvTemplateData,
  DownloadCsvTemplateType,
  matchSegmentAddRulesCsvTemplateMap,
  matchSegmentDeleteRulesCsvTemplateMap,
  matchSegmentRulesAddCsvTemplateData,
  matchSegmentRulesDeleteCsvTemplateData,
  roleCsvTemplateData,
  rolesCsvTemplateMap,
} from '@main-client/src/app/download-csv-template/download-csv-template.helper';
import { FeedMatchConfigUpdate } from '@main-client/src/app/feed/feed.actions';
import {
  UploadCSVListDrawerData,
  UploadItem,
} from '@main-client/src/app/upload-csv-list-drawer/upload-csv-list-drawer';
import {
  buildAddEdgesUpdateFromCsvRows,
  buildAddEdgesUploadResultsFromCsvRows,
  buildAddMatchSegmentsUploadResultsFromCsvRows,
  buildAddMatchSegmentUpdateForRoleFromCsvRows,
  buildDeleteEdgesUpdateFromCsvRows,
  buildDeleteEdgesUploadResultsFromCsvRows,
  buildDeleteMatchSegmentsUploadResultsFromCsvRows,
  buildDeleteMatchSegmentUpdateForRoleFromCsvRows,
  CustomSegmentCsvRow,
  findCustomSegmentConfigById,
  findMatchSegmentByRole,
  getValidCsvRowsGroupedByRole,
  MatchSegmentCsvRow,
} from '@main-client/src/app/upload-csv-list-drawer/upload-csv-list-drawer.helper';

import find from 'lodash-es/find';
import findIndex from 'lodash-es/findIndex';
import flatten from 'lodash-es/flatten';
import head from 'lodash-es/head';
import includes from 'lodash-es/includes';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import isUndefined from 'lodash-es/isUndefined';
import keys from 'lodash-es/keys';
import _map from 'lodash-es/map';
import mapValues from 'lodash-es/mapValues';
import pick from 'lodash-es/pick';
import set from 'lodash-es/set';
import values from 'lodash-es/values';

interface SegmentPayload {
  customSegmentConfig: MatchConfigCustomSegmentConfig;
  group: Group;
  matchConfig: MatchConfig;
  update: string[][];
}

interface RoleCsvRow {
  inviteable: string;
  name: string;
  opted_into_matchmaking: string;
  searchable: string;
}
@Injectable()
export class UploadCsvListDrawerService {
  constructor(
    private readonly drawerService: NzDrawerService,
    private readonly groupsBackend: GroupsBackend,
    private readonly store: Store<IAppState>,
  ) {}

  openUploadCSVListDrawer(data: UploadCSVListDrawerData) {
    return this.drawerService.create({
      nzBodyStyle: { padding: 0 },
      nzContent: UploadCsvListDrawerComponent,
      nzData: { data },
      nzTitle: data.title,
      nzWidth: '500px',
    }).afterClose;
  }

  private addCustomSegmentItems(
    group: Group,
    customSegment: GroupCustomSegment,
    nodes: { name: string }[] = [],
  ) {
    const results: { value: { name: string }; error?: any }[] = [];
    return concat(
      ...nodes.map((node: { name: string }) =>
        this.groupsBackend
          .postCustomSegmentsNode(group, customSegment, {
            name: node.name.trim(),
          })
          .pipe(
            this.handleCustomSegmentItemsChangeSuccess(group, node),
            this.handleChangeError(node),
          ),
      ),
    ).pipe(
      // FIXME: should use `reduce`
      map((result) => {
        results.push(result);
        return results;
      }),
      filter(
        (allResults: UploadItem<{ name: string }>[]) =>
          nodes.length === allResults.length,
      ),
      this.updateDrawerAddComplete(),
    );
  }

  private deleteCustomSegmentItems(
    group: Group,
    customSegment: GroupCustomSegment,
    nodes: { name: string }[] = [],
  ) {
    const results: { value: { name: string }; error?: any }[] = [];
    return concat(
      ...nodes.map((node: { name: string }) => {
        const nodeToDelete = find(customSegment.nodes, {
          name: node.name.trim(),
        });
        if (!nodeToDelete) {
          return of({
            error: `${node.name} does not exist in "${customSegment.title}"`,
            value: node,
          });
        }
        return this.groupsBackend
          .deleteCustomSegmentsNode(group, customSegment, nodeToDelete)
          .pipe(
            this.handleCustomSegmentItemsChangeSuccess(group, node),
            this.handleChangeError(node),
          );
      }),
    ).pipe(
      // FIXME: use reduce
      map((result) => {
        results.push(result);
        return results;
      }),
      filter((allResults: UploadItem[]) => nodes.length === allResults.length),
      tap(() => this.store.dispatch(ReFetchGroupMatchConfigs({ group }))),
      this.updateDrawerDeleteComplete(),
    );
  }

  private updateDrawerAddComplete<T>() {
    return map((results: UploadItem<T>[]) => {
      const errorItems = results.filter((result: UploadItem) => result.error);
      const numSuccessResults = results.length - errorItems.length;
      const description =
        0 < errorItems.length
          ? $localize`:@@uploadCsvListDrawer.addItemsPartialSuccess:${numSuccessResults} items were added successfully. Please note the following items were not added:`
          : $localize`:@@uploadCsvListDrawer.addItemSuccess:${numSuccessResults} items were added successfully.`;
      return {
        description: () => description,
        items: errorItems,
      };
    });
  }

  private updateDrawerDeleteComplete() {
    return map((results: UploadItem[]) => {
      const errorItems = results.filter((result: UploadItem) => result.error);
      const numSuccessResults = results.length - errorItems.length;
      const description =
        0 < errorItems.length
          ? $localize`:@@uploadCsvListDrawer.deleteItemsPartialSuccess:${numSuccessResults} items were deleted successfully. Please note the following items were not deleted:`
          : $localize`:@@uploadCsvListDrawer.deleteItemSuccess:${numSuccessResults} items were deleted successfully.`;
      return {
        description: () => description,
        items: errorItems,
      };
    });
  }

  private handleCustomSegmentItemsChangeSuccess(
    group: Group,
    node: { name: string },
  ) {
    return map((response: GroupCustomSegment[]) => {
      this.store.dispatch(
        UpdateGroupSuccess({
          group: { ...group, custom_segments: response },
        }),
      );
      return { value: node };
    });
  }

  private handleChangeError<T>(
    item: T,
  ): OperatorFunction<{ value: T }, { value: T; error?: any }> {
    return catchError((response: HttpErrorResponse) =>
      of({
        error: response.error.message,
        value: item,
      }),
    );
  }

  private getRolesTemplateData(roles: GroupRole[] = []) {
    return isEmpty(roles)
      ? roleCsvTemplateData
      : [
          keys(rolesCsvTemplateMap),
          ...roles.map((role: GroupRole) =>
            values(
              pick({ ...role.defaults, ...role }, values(rolesCsvTemplateMap)),
            ).map((columnValue: any) => `${columnValue}`),
          ),
        ];
  }

  private getCustomSegmentItemsTemplateData(customSegment: GroupCustomSegment) {
    return isEmpty(customSegment)
      ? customSegmentItemsCsvTemplateData
      : [
          keys(customSegmentCsvTemplateMap),
          ...(customSegment.nodes?.map((node: GroupCustomSegmentNode) => [
            node.name ?? '',
          ]) ?? []),
        ];
  }

  private getAddMatchSegmentRulesTemplateData(
    roles: GroupRole[],
    matchConfig: MatchConfig,
  ) {
    if (isEmpty(roles)) {
      return matchSegmentRulesAddCsvTemplateData;
    }
    const roleNames = this.getRoleNames(roles);
    const templateData = this.getMatchSegmentRulesTemplateData(
      keys(matchSegmentAddRulesCsvTemplateMap),
      roleNames,
      matchConfig.match_segments ?? [],
    );
    set(templateData, ['0', '2'], 'How to fill this out:');
    set(
      templateData,
      ['1', '2'],
      'In Column A enter all the roles in your introductions configuration',
    );
    set(
      templateData,
      ['2', '2'],
      'In Column B enter all the roles you want to add intro rules for - with respect to the role in Column A',
    );
    return templateData;
  }

  private getDeleteMatchSegmentRulesTemplateData(
    roles: GroupRole[],
    matchConfig: MatchConfig,
  ) {
    if (isEmpty(roles)) {
      return matchSegmentRulesDeleteCsvTemplateData;
    }
    const roleNames = this.getRoleNames(roles);
    const templateData = this.getMatchSegmentRulesTemplateData(
      keys(matchSegmentDeleteRulesCsvTemplateMap),
      roleNames,
      matchConfig.match_segments ?? [],
    );
    set(templateData, ['0', '2'], 'How to fill this out:');
    set(
      templateData,
      ['1', '2'],
      'In Column A enter all the roles in your introductions configuration',
    );
    set(
      templateData,
      ['2', '2'],
      'In Column B enter all the roles you want to delete intro rules for - with respect to the role in Column A',
    );
    return templateData;
  }

  private getRoleNames(roles: GroupRole[]) {
    return roles
      .map((role) => role.name)
      .filter((name): name is NonNullable<typeof name> => !isUndefined(name));
  }

  private getMatchSegmentRulesTemplateData(
    headers: string[],
    roleNames: string[] = [],
    matchSegments: MatchSegment[],
  ) {
    return [
      headers,
      ...flatten(
        roleNames.map((role: string) => {
          const matchSegmentForRole = find(
            matchSegments,
            (matchSegment: MatchSegment) => role === matchSegment.value,
          );
          if (isEmpty(matchSegmentForRole)) {
            return [[role, '']];
          }
          return (
            matchSegmentForRole?.match_with?.map((matchWith: string) => [
              role,
              matchWith,
            ]) ?? [[role, '']]
          );
        }),
      ),
    ];
  }

  private getAddCustomSegmentRulesTemplateData(
    customSegment: GroupCustomSegment,
    customSegmentConfigs: MatchConfigCustomSegmentConfig[],
  ) {
    const customSegmentConfig = findCustomSegmentConfigById(
      customSegment._id,
      customSegmentConfigs,
    );
    if (isEmpty(customSegmentConfig)) {
      return customSegmentRulesAddCsvTemplateData;
    }
    const nodes = this.getCustomSegmentNodeNames(customSegment.nodes);
    const templateData = this.getCustomSegmentRulesTemplateData(
      keys(customSegmentAddRulesCsvTemplateMap),
      nodes,
      customSegmentConfig?.edges ?? [],
    );
    set(templateData, ['0', '2'], 'How to fill this out:');
    set(
      templateData,
      ['1', '2'],
      'In Column A enter all the custom segments in your introductions configuration',
    );
    set(
      templateData,
      ['2', '2'],
      'In Column B enter all the custom segments you want to add intro rules for - with respect to the custom segment in Column A',
    );
    return templateData;
  }

  private getDeleteCustomSegmentRulesTemplateData(
    customSegment: GroupCustomSegment,
    customSegmentConfigs: MatchConfigCustomSegmentConfig[],
  ) {
    const customSegmentConfig = findCustomSegmentConfigById(
      customSegment._id,
      customSegmentConfigs,
    );
    if (isEmpty(customSegmentConfig)) {
      return customSegmentRulesDeleteCsvTemplateData;
    }
    const nodes = this.getCustomSegmentNodeNames(customSegment.nodes);
    const templateData = this.getCustomSegmentRulesTemplateData(
      keys(customSegmentDeleteRulesCsvTemplateMap),
      nodes,
      customSegmentConfig?.edges ?? [],
    );
    set(templateData, ['0', '2'], 'How to fill this out:');
    set(
      templateData,
      ['1', '2'],
      'In Column A enter the all custom segments in your introductions configuration',
    );
    set(
      templateData,
      ['2', '2'],
      'In Column B enter all the custom segments you want to delete intro rules for - with respect to the custom segment in Column A',
    );
    return templateData;
  }

  private getCustomSegmentNodeNames(
    customSegmentNodes: GroupCustomSegmentNode[] | undefined,
  ) {
    return (
      customSegmentNodes?.map(
        (node: GroupCustomSegmentNode) => node.name ?? '',
      ) ?? []
    );
  }

  private getCustomSegmentRulesTemplateData(
    headers: string[],
    nodes: string[] = [],
    customSegmentConfigEdges: string[][],
  ) {
    return [
      headers,
      ...flatten(
        nodes.map((node: string) => {
          const customSegmentEdgesForNode = customSegmentConfigEdges.filter(
            (edges: string[]) => includes(edges, node),
          );
          if (isEmpty(customSegmentEdgesForNode)) {
            return [[node, '']];
          }
          const nodesToMatchWith = customSegmentEdgesForNode.map(
            (edges: string[]) =>
              head(edges.filter((edge: string) => edge !== node)),
          );
          return nodesToMatchWith.map((nodeToMatchWith: string) => [
            node,
            nodeToMatchWith ? nodeToMatchWith : node,
          ]);
        }),
      ),
    ];
  }

  openAddRolesCsvDrawer(group: Group) {
    const drawerData: UploadCSVListDrawerData = {
      cta: $localize`:@@uploadCsvListDrawer.bulkAddRoles.cta:Add roles`,
      customText: {
        downloadInstruction: $localize`:@@uploadCsvListDrawer.bulkAddRoles.downloadInstruction:Add roles to the CSV file.`,
        downloadTemplateCta: $localize`:@@uploadCsvListDrawer.bulkAddRoles.downloadTemplateCta:Download roles CSV`,
        uploadInstruction: $localize`:@@uploadCsvListDrawer.bulkAddRoles.uploadInstruction:Upload file to add new roles to your community.`,
      },
      description: (itemCount) =>
        $localize`:@@uploadCsvListDrawer.bulkAddRoles.description:The following ${itemCount} items will be added in order to "Roles":`,
      submit: (roles: RoleCsvRow[]) => this.addRoles(group, roles),
      templateData: this.getRolesTemplateData(group.roles),
      templateTitle: csvTemplateTitle[DownloadCsvTemplateType.role],
      templateType: DownloadCsvTemplateType.role,
      title: $localize`:@@uploadCsvListDrawer.bulkAddRoles.title:Bulk add roles`,
    };
    return this.openUploadCSVListDrawer(drawerData);
  }

  openDeleteRolesCsvDrawer(group: Group) {
    const drawerData: UploadCSVListDrawerData = {
      cta: $localize`:@@uploadCsvListDrawer.bulkDeleteRoles.cta:Delete roles`,
      customText: {
        downloadInstruction: $localize`:@@uploadCsvListDrawer.bulkDeleteRoles.downloadInstruction:Remove roles from the CSV file.`,
        downloadTemplateCta: $localize`:@@uploadCsvListDrawer.bulkDeleteRoles.downloadTemplateCta:Download roles CSV`,
        uploadInstruction: $localize`:@@uploadCsvListDrawer.bulkDeleteRoles.uploadInstruction:Upload file to delete the roles from your community.`,
        warningText: $localize`:@@uploadCsvListDrawer.bulkDeleteRoles.warningText:Deleted roles cannot be restored.`,
      },
      description: (itemCount) =>
        $localize`:@@uploadCsvListDrawer.bulkDeleteRoles.description:The following ${itemCount} items will be deleted from "Roles":`,
      submit: (roles: RoleCsvRow[]) => this.deleteRoles(group, roles),
      templateData: this.getRolesTemplateData(group.roles),
      templateTitle: csvTemplateTitle[DownloadCsvTemplateType.role],
      templateType: DownloadCsvTemplateType.role,
      title: $localize`:@@uploadCsvListDrawer.bulkDeleteRoles.title:Bulk delete roles`,
    };
    return this.openUploadCSVListDrawer(drawerData);
  }

  private addRoles(group: Group, roles: RoleCsvRow[]) {
    const results: { value: RoleCsvRow; error?: any }[] = [];
    return concat(
      ...roles.map((role: RoleCsvRow) => {
        const roleBody = {
          defaults: {
            expert_status: 'approved',
            ...mapValues(
              pick(role, [
                'inviteable',
                'opted_into_matchmaking',
                'searchable',
              ]),
              (value) =>
                value ? 'true' === value.trim().toLowerCase() : undefined,
            ),
          },
          name: role.name.trim(),
        };
        return this.groupsBackend
          .postRoles(group, roleBody)
          .pipe(
            this.handleRolesChangeSuccess(group, role),
            this.handleChangeError(role),
          );
      }),
    ).pipe(
      map((result) => {
        results.push(result);
        return results;
      }),
      filter((allResults: UploadItem[]) => roles.length === allResults.length),
      this.updateDrawerAddComplete(),
    );
  }

  private deleteRoles(group: Group, roles: RoleCsvRow[]) {
    const results: { value: RoleCsvRow; error?: any }[] = [];
    return concat(
      ...roles.map((role: RoleCsvRow) => {
        const roleToDelete = find(group.roles, { name: role.name.trim() });
        if (!roleToDelete) {
          return of({
            error: `${role.name} does not exist in "Roles"`,
            value: role,
          });
        }
        return this.groupsBackend
          .deleteRoles(group, roleToDelete)
          .pipe(
            this.handleRolesChangeSuccess(group, role),
            this.handleChangeError(role),
          );
      }),
    ).pipe(
      // FIXME: use reduce
      map((result) => {
        results.push(result);
        return results;
      }),
      filter((allResults: UploadItem[]) => roles.length === allResults.length),
      this.updateDrawerDeleteComplete(),
    );
  }

  private handleRolesChangeSuccess(group: Group, role: RoleCsvRow) {
    return map((response: GroupRole[]) => {
      this.store.dispatch(
        UpdateGroupRoleSuccess({
          group,
          roles: response,
        }),
      );
      return { value: role };
    });
  }

  openAddCustomSegmentRulesCsvDrawer(
    group: Group,
    customSegment: GroupCustomSegment,
    matchConfig: MatchConfig,
    isPrivateSegment?: boolean,
  ) {
    const drawerData = {
      cta: $localize`:@@uploadCsvListDrawer.bulkAddRules.cta:Add rules`,
      customText: {
        downloadInstruction: $localize`:@@uploadCsvListDrawer.bulkAddCustomSegmentRules.downloadInstruction:Add custom segment rules to the CSV file.`,
        downloadTemplateCta: $localize`:@@uploadCsvListDrawer.bulkAddRules.downloadTemplateCta:Download rules CSV`,
        uploadInstruction: $localize`:@@uploadCsvListDrawer.bulkAddCustomSegmentRules.uploadInstruction:Upload file to add new custom segment rules to your community.`,
      },
      description: (itemCount: number) =>
        $localize`:@@uploadCsvListDrawer.addCustomSegmentRules.description:The following ${itemCount} rules will be added to "${customSegment.title}":`,
      submit: (csvRows: CustomSegmentCsvRow[]) =>
        this.addCustomSegmentEdges(
          group,
          csvRows,
          customSegment,
          matchConfig,
          isPrivateSegment,
        ),
      templateData: this.getAddCustomSegmentRulesTemplateData(
        customSegment,
        (isPrivateSegment
          ? matchConfig.hidden_custom_segment_configs
          : matchConfig.custom_segment_configs) ?? [],
      ),
      templateTitle:
        csvTemplateTitle[DownloadCsvTemplateType.customSegmentRulesAdd],
      templateType: DownloadCsvTemplateType.customSegmentRulesAdd,
      title: $localize`:@@uploadCsvListDrawer.bulkAddRules.title:Bulk add rules`,
    };
    return this.openUploadCSVListDrawer(drawerData);
  }

  openDeleteCustomSegmentRulesCsvDrawer(
    group: Group,
    customSegment: GroupCustomSegment,
    matchConfig: MatchConfig,
    isPrivateSegment?: boolean,
  ) {
    const drawerData = {
      cta: $localize`:@@uploadCsvListDrawer.bulkDeleteRules.cta:Delete rules`,
      customText: {
        downloadInstruction: $localize`:@@uploadCsvListDrawer.bulkDeleteCustomSegmentRules.downloadInstruction:Remove custom segment rules from the CSV file.`,
        downloadTemplateCta: $localize`:@@uploadCsvListDrawer.bulkDeleteRules.downloadTemplateCta:Download rules CSV`,
        uploadInstruction: $localize`:@@uploadCsvListDrawer.bulkDeleteCustomSegmentRules.uploadInstruction:Upload file to remove the custom segment rules from your community.`,
        warningText: $localize`:@@uploadCsvListDrawer.bulkDeleteRules.warningText:Deleted rules cannot be restored.`,
      },
      description: (itemCount: number) =>
        $localize`:@@uploadCsvListDrawer.bulkDeleteCustomSegmentRules.description:The following ${itemCount} rules will be deleted from "${customSegment.title}":`,
      submit: (csvRows: CustomSegmentCsvRow[]) =>
        this.deleteCustomSegmentEdges(
          group,
          csvRows,
          customSegment,
          matchConfig,
          isPrivateSegment,
        ),
      templateData: this.getDeleteCustomSegmentRulesTemplateData(
        customSegment,
        (isPrivateSegment
          ? matchConfig.hidden_custom_segment_configs
          : matchConfig.custom_segment_configs) ?? [],
      ),
      templateTitle:
        csvTemplateTitle[DownloadCsvTemplateType.customSegmentRulesDelete],
      templateType: DownloadCsvTemplateType.customSegmentRulesDelete,
      title: $localize`:@@uploadCsvListDrawer.bulkDeleteRules.title:Bulk delete rules`,
    };
    return this.openUploadCSVListDrawer(drawerData);
  }

  private segmentObservable(
    isPrivateSegment: boolean,
    segmentPayload: SegmentPayload,
  ) {
    const { group, matchConfig, customSegmentConfig, update } = segmentPayload;
    return isPrivateSegment
      ? this.groupsBackend.putMatchConfigHiddenCustomSegmentsConfigs(
          group,
          matchConfig,
          customSegmentConfig,
          { edges: update },
        )
      : this.groupsBackend.putMatchConfigCustomSegmentsConfigs(
          group,
          matchConfig,
          customSegmentConfig,
          { edges: update },
        );
  }

  private deleteCustomSegmentEdges(
    group: Group,
    csvRows: CustomSegmentCsvRow[],
    customSegment: GroupCustomSegment,
    matchConfig: MatchConfig,
    isPrivateSegment?: boolean,
  ) {
    const customSegmentConfig = findCustomSegmentConfigById(
      customSegment._id,
      (isPrivateSegment
        ? matchConfig.hidden_custom_segment_configs
        : matchConfig.custom_segment_configs) ?? [],
    );
    if (!customSegmentConfig) {
      return of([]).pipe(this.updateDrawerDeleteComplete());
    }
    const edges = customSegmentConfig?.edges ?? [];
    const uploadResults = buildDeleteEdgesUploadResultsFromCsvRows(
      csvRows,
      customSegment,
      edges,
    );
    const update = buildDeleteEdgesUpdateFromCsvRows(
      csvRows,
      customSegment,
      edges,
    );
    if (isEqual(edges, update)) {
      return of(uploadResults).pipe(this.updateDrawerDeleteComplete());
    }
    const segmentObservable$ = this.segmentObservable(isPrivateSegment, {
      customSegmentConfig,
      group,
      matchConfig,
      update,
    });
    return segmentObservable$.pipe(
      this.handleCustomSegmentRulesChangeSuccess(
        group,
        matchConfig,
        isPrivateSegment,
      ),
      map(() => uploadResults),
      this.handleCustomSegmentRulesChangeError(uploadResults),
      this.updateDrawerDeleteComplete(),
    );
  }

  private addCustomSegmentEdges(
    group: Group,
    csvRows: CustomSegmentCsvRow[],
    customSegment: GroupCustomSegment,
    matchConfig: MatchConfig,
    isPrivateSegment?: boolean,
  ) {
    const customSegmentConfig = findCustomSegmentConfigById(
      customSegment._id,
      (isPrivateSegment
        ? matchConfig.hidden_custom_segment_configs
        : matchConfig.custom_segment_configs) ?? [],
    );
    if (!customSegmentConfig) {
      return of([]).pipe(this.updateDrawerDeleteComplete());
    }
    const edges = customSegmentConfig?.edges ?? [];
    const uploadResults = buildAddEdgesUploadResultsFromCsvRows(
      csvRows,
      customSegment,
      edges,
    );
    const updateEdges = buildAddEdgesUpdateFromCsvRows(
      csvRows,
      customSegment,
      edges,
    );
    if (isEmpty(updateEdges)) {
      return of(uploadResults).pipe(this.updateDrawerAddComplete());
    }
    const update = [...edges, ...updateEdges];
    const segmentObservable$ = this.segmentObservable(isPrivateSegment, {
      customSegmentConfig,
      group,
      matchConfig,
      update,
    });
    return segmentObservable$.pipe(
      this.handleCustomSegmentRulesChangeSuccess(
        group,
        matchConfig,
        isPrivateSegment,
      ),
      map(() => uploadResults),
      this.handleCustomSegmentRulesChangeError(uploadResults),
      this.updateDrawerAddComplete(),
    );
  }

  private handleCustomSegmentRulesChangeSuccess(
    group: Group,
    matchConfig: MatchConfig,
    isPrivateSegment?: boolean,
  ) {
    return tap((customSegmentConfig: MatchConfigCustomSegmentConfig) => {
      this.store.dispatch(
        FeedMatchConfigUpdate({
          groupId: group._id,
          update: updateCustomSegmentConfigInMatchConfig(
            matchConfig,
            customSegmentConfig,
            isPrivateSegment,
          ),
        }),
      );
      this.store.dispatch(
        UpdateGroupMatchConfigCustomSegmentConfigsSuccess({
          customSegmentConfig,
          group,
          matchConfig,
          isPrivateSegment,
        }),
      );
    });
  }

  private handleCustomSegmentRulesChangeError(results: UploadItem[]) {
    return catchError((response: HttpErrorResponse) =>
      of(
        results.map((edge) => ({
          ...edge,
          error: edge.error || response.error.message,
        })),
      ),
    );
  }

  openAddMatchSegmentRulesCsvDrawer(group: Group, matchConfig: MatchConfig) {
    const drawerData = {
      cta: $localize`:@@uploadCsvListDrawer.bulkAddRules.cta:Add rules`,
      customText: {
        downloadInstruction: $localize`:@@uploadCsvListDrawer.bulkAddIntroRules.downloadInstruction:Add introduction rules to the CSV file.`,
        downloadTemplateCta: $localize`:@@uploadCsvListDrawer.bulkAddRules.downloadTemplateCta:Download rules CSV`,
        uploadInstruction: $localize`:@@uploadCsvListDrawer.bulkAddIntroRules.uploadInstruction:Upload file to add new introduction rules to your community.`,
      },
      description: (itemCount: number) =>
        $localize`:@@uploadCsvListDrawer.bulkAddRules.description:The following ${itemCount} rules will be added to "Roles":`,
      submit: (csvRows: MatchSegmentCsvRow[]) =>
        this.addMatchSegmentRules(csvRows, group, matchConfig),
      templateData: this.getAddMatchSegmentRulesTemplateData(
        group.roles ?? [],
        matchConfig,
      ),
      templateTitle:
        csvTemplateTitle[DownloadCsvTemplateType.matchSegmentRulesAdd],
      templateType: DownloadCsvTemplateType.matchSegmentRulesAdd,
      title: $localize`:@@uploadCsvListDrawer.bulkAddRules.title:Upload rules`,
    };
    return this.openUploadCSVListDrawer(drawerData);
  }

  openDeleteMatchSegmentRulesCsvDrawer(group: Group, matchConfig: MatchConfig) {
    const drawerData = {
      cta: $localize`:@@uploadCsvListDrawer.bulkDeleteRules.cta:Delete rules`,
      customText: {
        downloadInstruction: $localize`:@@uploadCsvListDrawer.bulkDeleteIntroRules.downloadInstruction:Remove introduction rules from the CSV file.`,
        downloadTemplateCta: $localize`:@@uploadCsvListDrawer.bulkDeleteRules.downloadTemplateCta:Download rules CSV`,
        uploadInstruction: $localize`:@@uploadCsvListDrawer.bulkDeleteIntroRules.uploadInstruction:Upload file to delete the introduction rules from your community.`,
        warningText: $localize`:@@uploadCsvListDrawer.bulkDeleteRules.warningText:Deleted rules cannot be restored`,
      },
      description: (itemCount: number) =>
        $localize`:@@uploadCsvListDrawer.bulkDeleteRules.description:The following ${itemCount} rules will be deleted from "Roles":`,
      submit: (csvRows: MatchSegmentCsvRow[]) =>
        this.deleteMatchSegmentRules(csvRows, group, matchConfig),
      templateData: this.getDeleteMatchSegmentRulesTemplateData(
        group.roles ?? [],
        matchConfig,
      ),
      templateTitle:
        csvTemplateTitle[DownloadCsvTemplateType.matchSegmentRulesDelete],
      templateType: DownloadCsvTemplateType.matchSegmentRulesDelete,
      title: $localize`:@@uploadCsvListDrawer.bulkDeleteRules.title:Delete rules`,
    };
    return this.openUploadCSVListDrawer(drawerData);
  }

  private addMatchSegmentRules(
    matchSegmentCsvRows: MatchSegmentCsvRow[],
    group: Group,
    matchConfig: MatchConfig,
  ) {
    const uploadResults = buildAddMatchSegmentsUploadResultsFromCsvRows(
      matchSegmentCsvRows,
      group.roles ?? [],
      matchConfig.match_segments ?? [],
    );
    const csvRowsGroupedByRole = getValidCsvRowsGroupedByRole(uploadResults);
    const matchSegmentUpdates = _map(
      csvRowsGroupedByRole,
      (csvRows: MatchSegmentCsvRow[], roleName: string) =>
        this.buildMatchSegmentAddUpdateForRole(
          csvRows,
          group,
          matchConfig,
          roleName,
        ).pipe(
          map(() => csvRows),
          this.handleMatchSegmentRulesChangeError(csvRows, uploadResults),
        ),
    );
    if (isEmpty(matchSegmentUpdates)) {
      return of(uploadResults).pipe(this.updateDrawerAddComplete());
    }
    const results: MatchSegmentCsvRow[][] = [];
    return concat(...matchSegmentUpdates).pipe(
      // FIXME: use reduce or something else instead
      map((result) => {
        results.push(result);
        return results;
      }),
      filter((allResults) => matchSegmentUpdates.length === allResults.length),
      map(() => uploadResults),
      this.updateDrawerAddComplete(),
    );
  }

  private deleteMatchSegmentRules(
    matchSegmentCsvRows: MatchSegmentCsvRow[],
    group: Group,
    matchConfig: MatchConfig,
  ) {
    const uploadResults = buildDeleteMatchSegmentsUploadResultsFromCsvRows(
      matchSegmentCsvRows,
      group.roles ?? [],
      matchConfig.match_segments ?? [],
    );
    const csvRowsGroupedByRole = getValidCsvRowsGroupedByRole(uploadResults);
    const matchSegmentUpdates = _map(
      csvRowsGroupedByRole,
      (csvRows: MatchSegmentCsvRow[], roleName: string) =>
        this.buildMatchSegmentDeleteUpdateForRole(
          csvRows,
          group,
          matchConfig,
          roleName,
        ).pipe(
          map(() => csvRows),
          this.handleMatchSegmentRulesChangeError(csvRows, uploadResults),
        ),
    );
    if (isEmpty(matchSegmentUpdates)) {
      return of(uploadResults).pipe(this.updateDrawerDeleteComplete());
    }
    const results: MatchSegmentCsvRow[][] = [];
    return concat(...matchSegmentUpdates).pipe(
      map((result) => {
        results.push(result);
        return results;
      }),
      filter((allResults) => matchSegmentUpdates.length === allResults.length),
      map(() => uploadResults),
      this.updateDrawerDeleteComplete(),
    );
  }

  private buildMatchSegmentAddUpdateForRole(
    csvRows: MatchSegmentCsvRow[],
    group: Group,
    matchConfig: MatchConfig,
    roleName: string,
  ): Observable<any> {
    const matchSegment = findMatchSegmentByRole(
      matchConfig.match_segments ?? [],
      roleName,
    );
    const update = buildAddMatchSegmentUpdateForRoleFromCsvRows(
      csvRows,
      matchSegment,
      roleName,
    );
    return matchSegment
      ? this.groupsBackend
          .patchGroupMatchSegment(group, matchConfig, matchSegment, update)
          .pipe(this.handleMatchSegmentRulesPatchSuccess(group, matchConfig))
      : this.groupsBackend
          .addGroupMatchSegment(group, matchConfig, update)
          .pipe(this.handleMatchSegmentRulesPostSuccess(group, matchConfig));
  }

  private buildMatchSegmentDeleteUpdateForRole(
    csvRows: MatchSegmentCsvRow[],
    group: Group,
    matchConfig: MatchConfig,
    roleName: string,
  ): Observable<any> {
    const matchSegment = findMatchSegmentByRole(
      matchConfig.match_segments ?? [],
      roleName,
    );
    if (!matchSegment) {
      // TODO: warn with logger
      return of({});
    }
    const update = buildDeleteMatchSegmentUpdateForRoleFromCsvRows(
      csvRows,
      matchSegment,
      roleName,
    );
    return 0 < (update.match_with?.length ?? 0)
      ? this.groupsBackend
          .patchGroupMatchSegment(group, matchConfig, matchSegment, update)
          .pipe(this.handleMatchSegmentRulesPatchSuccess(group, matchConfig))
      : this.groupsBackend
          .deleteGroupMatchSegment(group, matchConfig, matchSegment)
          .pipe(
            this.handleMatchSegmentRulesDeleteSuccess(
              group,
              matchConfig,
              matchSegment,
            ),
          );
  }

  private handleMatchSegmentRulesPostSuccess(
    group: Group,
    matchConfig: MatchConfig,
  ) {
    return tap((response: MatchSegment) => {
      this.store.dispatch(
        FeedMatchConfigUpdate({
          groupId: group._id,
          update: addMatchSegmentInMatchConfig(matchConfig, response),
        }),
      );
      this.store.dispatch(
        AddGroupMatchConfigSegmentsSuccess({
          group,
          matchConfig,
          matchSegment: response,
        }),
      );
    });
  }

  private handleMatchSegmentRulesPatchSuccess(
    group: Group,
    matchConfig: MatchConfig,
  ) {
    return tap((response: MatchConfigCustomSegmentConfig) => {
      this.store.dispatch(
        FeedMatchConfigUpdate({
          groupId: group._id,
          update: updateMatchSegmentInMatchConfig(matchConfig, response),
        }),
      );
      this.store.dispatch(
        UpdateGroupMatchConfigSegmentsSuccess({
          group,
          matchConfig,
          matchSegment: response,
        }),
      );
    });
  }

  private handleMatchSegmentRulesDeleteSuccess(
    group: Group,
    matchConfig: MatchConfig,
    matchSegment: MatchSegment,
  ) {
    return tap(() => {
      this.store.dispatch(
        FeedMatchConfigUpdate({
          groupId: group._id,
          update: removeMatchSegmentInMatchConfig(matchConfig, matchSegment),
        }),
      );
      this.store.dispatch(
        RemoveGroupMatchConfigSegmentsSuccess({
          group,
          matchConfig,
          matchSegment,
        }),
      );
    });
  }

  private handleMatchSegmentRulesChangeError(
    csvRows: MatchSegmentCsvRow[],
    uploadResults: UploadItem[],
  ): OperatorFunction<MatchSegmentCsvRow[], MatchSegmentCsvRow[]> {
    return catchError((response: HttpErrorResponse) => {
      csvRows.forEach((csvRow: MatchSegmentCsvRow) => {
        const index = findIndex(
          uploadResults,
          (uploadResult: UploadItem) =>
            csvRow.value === uploadResult.value.value,
        );
        uploadResults[index].error = response.error.message;
      });
      return of(csvRows);
    });
  }
}
